"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo, useState } from "react";
import { FiFlag } from "react-icons/fi";
import Tooltip from "@/components/ui/tooltip";
import { hashToColor } from "@/lib/measurement-calculations";
import { type ScoreBreakdown, type ScoreBreakdownEntry } from "@/lib/measurement-score-breakdown";
import type { MeasurementField } from "@/types/measurement";
interface ScoreDonutChartProps {
  breakdown: ScoreBreakdown;
  title: string;
  score: number | null;
  unit?: string | null;
  decimalPlaces?: number | null;
  outcomeLabel?: string | null;
  colorsByKey?: Record<string, string>;
  fieldMap?: Map<string, MeasurementField>;
}
interface ArcEntry extends ScoreBreakdownEntry {
  color: string;
  startAngle: number;
  endAngle: number;
  fillEndAngle: number;
}
const CENTER = 60;
const RADIUS = 44;
const STROKE_WIDTH = 10;
const MAX_SCORE = 100;
const START_ANGLE = 90;
function clampScore(score: number | null): number {
  if (score == null || !Number.isFinite(score)) return 0;
  return Math.max(0, Math.min(MAX_SCORE, score));
}
function formatFixed(value: number, decimalPlaces = 0): string {
  const bounded = Math.max(0, Math.min(4, decimalPlaces));
  return value.toFixed(bounded);
}
function formatScore(value: number | null, decimalPlaces = 0): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return formatFixed(value, decimalPlaces);
}
function formatWeight(value: number): string {
  if (Math.abs(value - Math.round(value)) < 0.01) return `${Math.round(value)}%`;
  return `${formatFixed(value, 1)}%`;
}
function centerTitle(title: string): string {
  const withoutSuffix = title.replace(/\s+(?:health\s+)?score$/i, "").trim();
  return withoutSuffix || title;
}
function findOptionLabel(field: MeasurementField | undefined, value: string | number | boolean): string | null {
  const option = field?.options?.find(candidate => String(candidate.value) === String(value));
  return option?.label ?? null;
}
function isMachineLabel(label: string): boolean {
  const trimmed = label.trim();
  if (!trimmed) return true;
  if (/\s/.test(trimmed)) return false;
  return /^[a-z0-9_-]+$/.test(trimmed);
}
function displayEntryLabel(entry: ScoreBreakdownEntry, field: MeasurementField | undefined): string {
  if (field?.name && isMachineLabel(entry.label)) return field.name;
  return entry.label;
}
function formatEntryValue(entry: ScoreBreakdownEntry, field: MeasurementField | undefined): string {
  if (entry.value == null) return "No value";
  const optionLabel = findOptionLabel(field, entry.value);
  if (optionLabel) return optionLabel;
  if (typeof entry.value === "boolean") return entry.value ? "Yes" : "No";
  if (typeof entry.value === "string") return entry.value;
  const decimalPlaces = field?.decimalPlaces ?? 1;
  const formatted = formatFixed(entry.value, decimalPlaces);
  return field?.unit ? `${formatted} ${field.unit}` : formatted;
}
function polarToCartesian(angle: number) {
  const radians = (angle - 90) * Math.PI / 180;
  return {
    x: CENTER + RADIUS * Math.cos(radians),
    y: CENTER + RADIUS * Math.sin(radians)
  };
}
function describeArc(startAngle: number, endAngle: number): string | null {
  const span = endAngle - startAngle;
  if (span <= 0.01) return null;
  const safeEndAngle = span >= 360 ? startAngle + 359.99 : endAngle;
  const start = polarToCartesian(startAngle);
  const end = polarToCartesian(safeEndAngle);
  const largeArcFlag = safeEndAngle - startAngle > 180 ? 1 : 0;
  return [`M ${start.x.toFixed(3)} ${start.y.toFixed(3)}`, `A ${RADIUS} ${RADIUS} 0 ${largeArcFlag} 1 ${end.x.toFixed(3)} ${end.y.toFixed(3)}`].join(" ");
}
function buildArcEntries(entries: ScoreBreakdownEntry[], colorsByKey: Record<string, string> | undefined): ArcEntry[] {
  let cursor = 0;
  return entries.map(entry => {
    const span = entry.weightPercent / 100 * 360;
    const gap = Math.min(2, span * 0.25);
    const startAngle = START_ANGLE + cursor + gap / 2;
    const endAngle = START_ANGLE + cursor + span - gap / 2;
    const fillFraction = clampScore(entry.score) / MAX_SCORE;
    const fillEndAngle = startAngle + (endAngle - startAngle) * fillFraction;
    cursor += span;
    return {
      ...entry,
      color: colorsByKey?.[entry.key] ?? (entry.operandId ? colorsByKey?.[entry.operandId] : undefined) ?? hashToColor(entry.operandId ?? entry.key),
      startAngle,
      endAngle,
      fillEndAngle
    };
  });
}
function EntryDetailTooltip(t0) {
  const $ = _c(21);
  const {
    entry,
    valueLabel,
    displayLabel
  } = t0;
  let t1;
  if ($[0] !== displayLabel) {
    t1 = <p className="text-caption-emphasis">{displayLabel}</p>;
    $[0] = displayLabel;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== entry.score) {
    t2 = formatScore(entry.score);
    $[2] = entry.score;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <p className="text-caption">Score: {t2}</p>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== valueLabel) {
    t4 = <p className="text-caption">Value: {valueLabel}</p>;
    $[6] = valueLabel;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== entry.sourceLabel) {
    t5 = <p className="text-caption">Source: {entry.sourceLabel}</p>;
    $[8] = entry.sourceLabel;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== entry.fallbackFrom) {
    t6 = entry.fallbackFrom && <p className="text-caption">From {entry.fallbackFrom}</p>;
    $[10] = entry.fallbackFrom;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== entry.warnings) {
    t7 = entry.warnings.map(_temp);
    $[12] = entry.warnings;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t1 || $[15] !== t3 || $[16] !== t4 || $[17] !== t5 || $[18] !== t6 || $[19] !== t7) {
    t8 = <div className="space-y-1.5">{t1}{t3}{t4}{t5}{t6}{t7}</div>;
    $[14] = t1;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  return t8;
}
function _temp(warning) {
  return <p key={warning} className="text-caption text-status-warning-text">{warning}</p>;
}
function ScoreEntryChip(t0) {
  const $ = _c(43);
  const {
    entry,
    active,
    field,
    onActivate
  } = t0;
  let t1;
  if ($[0] !== entry || $[1] !== field) {
    t1 = formatEntryValue(entry, field);
    $[0] = entry;
    $[1] = field;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const valueLabel = t1;
  let t2;
  if ($[3] !== entry || $[4] !== field) {
    t2 = displayEntryLabel(entry, field);
    $[3] = entry;
    $[4] = field;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const displayLabel = t2;
  const hasNotice = entry.source !== "direct" || entry.warnings.length > 0;
  let t3;
  if ($[6] !== displayLabel || $[7] !== entry || $[8] !== valueLabel) {
    t3 = <EntryDetailTooltip entry={entry} valueLabel={valueLabel} displayLabel={displayLabel} />;
    $[6] = displayLabel;
    $[7] = entry;
    $[8] = valueLabel;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== entry.key || $[11] !== onActivate) {
    t4 = () => onActivate(entry.key);
    $[10] = entry.key;
    $[11] = onActivate;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== onActivate) {
    t5 = () => onActivate(null);
    $[13] = onActivate;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== entry.key || $[16] !== onActivate) {
    t6 = () => onActivate(entry.key);
    $[15] = entry.key;
    $[16] = onActivate;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== onActivate) {
    t7 = () => onActivate(null);
    $[18] = onActivate;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== entry.color) {
    t8 = <span className="h-2 w-2 shrink-0 rounded-full" style={{
      backgroundColor: entry.color
    }} />;
    $[20] = entry.color;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== displayLabel) {
    t9 = <span className="min-w-0 truncate">{displayLabel}</span>;
    $[22] = displayLabel;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== entry.score) {
    t10 = formatScore(entry.score);
    $[24] = entry.score;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== t10) {
    t11 = <span className="shrink-0 tabular-nums text-foreground">{t10}</span>;
    $[26] = t10;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== hasNotice) {
    t12 = hasNotice && <span className="shrink-0 text-status-warning-text">•</span>;
    $[28] = hasNotice;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== active || $[31] !== t11 || $[32] !== t12 || $[33] !== t4 || $[34] !== t5 || $[35] !== t6 || $[36] !== t7 || $[37] !== t8 || $[38] !== t9) {
    t13 = <button type="button" onMouseEnter={t4} onMouseLeave={t5} onFocus={t6} onBlur={t7} className="inline-flex max-w-40 items-center gap-1.5 rounded-full border border-border bg-background-secondary px-2 py-1 text-caption text-foreground-secondary transition-colors hover:bg-hover focus:outline-none focus:ring-2 focus:ring-ring/20" aria-pressed={active}>{t8}{t9}{t11}{t12}</button>;
    $[30] = active;
    $[31] = t11;
    $[32] = t12;
    $[33] = t4;
    $[34] = t5;
    $[35] = t6;
    $[36] = t7;
    $[37] = t8;
    $[38] = t9;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  let t14;
  if ($[40] !== t13 || $[41] !== t3) {
    t14 = <Tooltip content={t3} side="top" triggerAsChild={true} variant="light">{t13}</Tooltip>;
    $[40] = t13;
    $[41] = t3;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  return t14;
}
function RedFlagIndicator(t0) {
  const $ = _c(21);
  const {
    breakdown,
    scorePrecision
  } = t0;
  const hasCap = breakdown.capReasons.length > 0;
  const hasWarnings = breakdown.warnings.length > 0;
  if (!hasCap && !hasWarnings) {
    return null;
  }
  const label = hasCap ? "Red flag cap applied" : "Calculation notice";
  let t1;
  if ($[0] !== label) {
    t1 = <p className="text-caption-emphasis">{label}</p>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== breakdown.capReasons || $[3] !== hasCap || $[4] !== scorePrecision) {
    t2 = hasCap && <div className="space-y-2">{breakdown.capReasons.map(reason => <div key={`${reason.cap}-${reason.reason}`} className="space-y-0.5"><p className="text-caption">Caused by: {reason.reason}</p><p className="text-tiny text-foreground-muted">Score is capped at {formatScore(reason.cap, scorePrecision)}.</p></div>)}</div>;
    $[2] = breakdown.capReasons;
    $[3] = hasCap;
    $[4] = scorePrecision;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== breakdown.warnings) {
    t3 = breakdown.warnings.map(_temp2);
    $[6] = breakdown.warnings;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t1 || $[9] !== t2 || $[10] !== t3) {
    t4 = <div className="space-y-1.5">{t1}{t2}{t3}</div>;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <FiFlag className="h-3 w-3" />;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== hasCap) {
    t6 = hasCap ? <span>{"Red flag"}</span> : <span>{"Notice"}</span>;
    $[13] = hasCap;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== label || $[16] !== t6) {
    t7 = <button type="button" aria-label={label} className="pointer-events-auto inline-flex items-center gap-1 rounded-full bg-status-error-bg px-2 py-0.5 text-tiny font-semibold text-status-error-text shadow-sm">{t5}{t6}</button>;
    $[15] = label;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t4 || $[19] !== t7) {
    t8 = <Tooltip content={t4} side="bottom" triggerAsChild={true} variant="light">{t7}</Tooltip>;
    $[18] = t4;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  return t8;
}
function _temp2(warning) {
  return <p key={warning} className="text-caption text-status-warning-text">{warning}</p>;
}
export default function ScoreDonutChart(t0) {
  const $ = _c(70);
  const {
    breakdown,
    title,
    score,
    unit,
    decimalPlaces,
    outcomeLabel,
    colorsByKey,
    fieldMap
  } = t0;
  const [activeKey, setActiveKey] = useState(null);
  let t1;
  if ($[0] !== breakdown.entries || $[1] !== colorsByKey) {
    t1 = buildArcEntries(breakdown.entries, colorsByKey);
    $[0] = breakdown.entries;
    $[1] = colorsByKey;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const arcEntries = t1;
  let activeEntry;
  let activeValueLabel;
  let scorePrecision;
  let t2;
  if ($[3] !== activeKey || $[4] !== arcEntries || $[5] !== decimalPlaces || $[6] !== fieldMap || $[7] !== title) {
    let t3;
    if ($[12] !== activeKey) {
      t3 = entry => entry.key === activeKey;
      $[12] = activeKey;
      $[13] = t3;
    } else {
      t3 = $[13];
    }
    activeEntry = arcEntries.find(t3);
    const activeField = activeEntry?.operandId ? fieldMap?.get(activeEntry.operandId) : undefined;
    activeValueLabel = activeEntry ? formatEntryValue(activeEntry, activeField) : null;
    scorePrecision = decimalPlaces ?? 0;
    t2 = activeEntry ? displayEntryLabel(activeEntry, activeField) : centerTitle(title);
    $[3] = activeKey;
    $[4] = arcEntries;
    $[5] = decimalPlaces;
    $[6] = fieldMap;
    $[7] = title;
    $[8] = activeEntry;
    $[9] = activeValueLabel;
    $[10] = scorePrecision;
    $[11] = t2;
  } else {
    activeEntry = $[8];
    activeValueLabel = $[9];
    scorePrecision = $[10];
    t2 = $[11];
  }
  const centerLabel = t2;
  const t3 = `${title} score breakdown`;
  let t4;
  if ($[14] !== activeKey || $[15] !== arcEntries || $[16] !== fieldMap) {
    let t5;
    if ($[18] !== activeKey || $[19] !== fieldMap) {
      t5 = entry_0 => {
        const trackPath = describeArc(entry_0.startAngle, entry_0.endAngle);
        const fillPath = describeArc(entry_0.startAngle, entry_0.fillEndAngle);
        const active = activeKey == null || activeKey === entry_0.key;
        const field = entry_0.operandId ? fieldMap?.get(entry_0.operandId) : undefined;
        const displayLabel = displayEntryLabel(entry_0, field);
        return <g key={entry_0.key} onMouseEnter={() => setActiveKey(entry_0.key)} onMouseLeave={() => setActiveKey(null)} onFocus={() => setActiveKey(entry_0.key)} onBlur={() => setActiveKey(null)} tabIndex={0}>{trackPath && <path d={trackPath} fill="none" stroke={entry_0.color} strokeLinecap="butt" strokeWidth={STROKE_WIDTH} opacity={active ? 0.22 : 0.12} />}{fillPath && <path d={fillPath} fill="none" stroke={entry_0.color} strokeLinecap="butt" strokeWidth={STROKE_WIDTH} opacity={active ? 0.95 : 0.45}><title>{displayLabel}: {formatScore(entry_0.score)} points, weight {formatWeight(entry_0.weightPercent)}</title></path>}</g>;
      };
      $[18] = activeKey;
      $[19] = fieldMap;
      $[20] = t5;
    } else {
      t5 = $[20];
    }
    t4 = arcEntries.map(t5);
    $[14] = activeKey;
    $[15] = arcEntries;
    $[16] = fieldMap;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  let t5;
  if ($[21] !== t3 || $[22] !== t4) {
    t5 = <svg viewBox="0 0 120 120" role="img" aria-label={t3} className="h-full w-full">{t4}</svg>;
    $[21] = t3;
    $[22] = t4;
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  let t6;
  if ($[24] !== centerLabel) {
    t6 = <span className="block w-full max-w-36 truncate text-caption-strong text-foreground-secondary">{centerLabel}</span>;
    $[24] = centerLabel;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  const t7 = activeEntry ? activeEntry.score : score;
  let t8;
  if ($[26] !== scorePrecision || $[27] !== t7) {
    t8 = formatScore(t7, scorePrecision);
    $[26] = scorePrecision;
    $[27] = t7;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  let t9;
  if ($[29] !== t8) {
    t9 = <span className="text-h1 tabular-nums text-foreground">{t8}</span>;
    $[29] = t8;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  let t10;
  if ($[31] !== activeValueLabel) {
    t10 = activeValueLabel && <span className="block w-full max-w-40 truncate text-caption text-foreground-secondary">{activeValueLabel}</span>;
    $[31] = activeValueLabel;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  const t11 = activeEntry ? "component score" : unit ?? "points";
  let t12;
  if ($[33] !== t11) {
    t12 = <span className="text-tiny text-foreground-muted">{t11}</span>;
    $[33] = t11;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  let t13;
  if ($[35] !== activeEntry || $[36] !== breakdown || $[37] !== scorePrecision) {
    t13 = !activeEntry && <RedFlagIndicator breakdown={breakdown} scorePrecision={scorePrecision} />;
    $[35] = activeEntry;
    $[36] = breakdown;
    $[37] = scorePrecision;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] !== t10 || $[40] !== t12 || $[41] !== t13 || $[42] !== t6 || $[43] !== t9) {
    t14 = <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center px-8 text-center">{t6}{t9}{t10}{t12}{t13}</div>;
    $[39] = t10;
    $[40] = t12;
    $[41] = t13;
    $[42] = t6;
    $[43] = t9;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  let t15;
  if ($[45] !== t14 || $[46] !== t5) {
    t15 = <div className="relative mx-auto h-60 w-60 max-w-full">{t5}{t14}</div>;
    $[45] = t14;
    $[46] = t5;
    $[47] = t15;
  } else {
    t15 = $[47];
  }
  let t16;
  if ($[48] !== activeKey || $[49] !== arcEntries || $[50] !== fieldMap) {
    let t17;
    if ($[52] !== activeKey || $[53] !== fieldMap) {
      t17 = entry_1 => <ScoreEntryChip key={entry_1.key} entry={entry_1} active={activeKey === entry_1.key} field={entry_1.operandId ? fieldMap?.get(entry_1.operandId) : undefined} onActivate={setActiveKey} />;
      $[52] = activeKey;
      $[53] = fieldMap;
      $[54] = t17;
    } else {
      t17 = $[54];
    }
    t16 = arcEntries.map(t17);
    $[48] = activeKey;
    $[49] = arcEntries;
    $[50] = fieldMap;
    $[51] = t16;
  } else {
    t16 = $[51];
  }
  let t17;
  if ($[55] !== t16) {
    t17 = <div className="flex flex-wrap justify-center gap-1.5">{t16}</div>;
    $[55] = t16;
    $[56] = t17;
  } else {
    t17 = $[56];
  }
  let t18;
  if ($[57] !== outcomeLabel) {
    t18 = outcomeLabel && <span className="inline-flex rounded-full bg-background-secondary px-2 py-0.5 text-tiny font-medium text-foreground-secondary">{outcomeLabel}</span>;
    $[57] = outcomeLabel;
    $[58] = t18;
  } else {
    t18 = $[58];
  }
  let t19;
  if ($[59] !== breakdown.cap || $[60] !== breakdown.rawScore || $[61] !== scorePrecision) {
    t19 = breakdown.rawScore != null && breakdown.cap != null && <p className="text-caption text-foreground-muted">Raw {formatScore(breakdown.rawScore, scorePrecision)} · cap{" "}{formatScore(breakdown.cap, scorePrecision)}</p>;
    $[59] = breakdown.cap;
    $[60] = breakdown.rawScore;
    $[61] = scorePrecision;
    $[62] = t19;
  } else {
    t19 = $[62];
  }
  let t20;
  if ($[63] !== t18 || $[64] !== t19) {
    t20 = <div className="space-y-1 text-center">{t18}{t19}</div>;
    $[63] = t18;
    $[64] = t19;
    $[65] = t20;
  } else {
    t20 = $[65];
  }
  let t21;
  if ($[66] !== t15 || $[67] !== t17 || $[68] !== t20) {
    t21 = <div className="rounded-xl border border-border bg-background p-4"><div className="space-y-3">{t15}{t17}{t20}</div></div>;
    $[66] = t15;
    $[67] = t17;
    $[68] = t20;
    $[69] = t21;
  } else {
    t21 = $[69];
  }
  return t21;
}